import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ACTIVITY_MOBILITY_OPTIONS, CARE_PLAN_TEXT_MAX_LENGTH, CARE_PLAN_TEXT_MAX_MESSAGE, CarePlanSectionProps, NA, NO, ORAL_HYGIENE_OPTIONS, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, TEETH_TYPE_OPTIONS, YES } from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE } from "../../../constants/carePlanConstants";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  ActivitiesOfDailyLivingGenieSectionValues,
  ADL_GENIE_FIELD_DEFINITIONS,
  ADL_GENIE_QUESTIONS,
} from "./activitiesOfDailyLivingGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const toStringArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item) => String(item || "").trim()).map((item) => String(item));
  }

  if (typeof value === "string") {
    return value
      .split(/[;,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const ActivitiesOfDailyLivingSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);

  const toast = useRef<Toast>(null);
  const oralHygieneCareValues = toStringArray(sectionData?.anyOralHygieneCare);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.ACTIVITIES_OF_DAILY_LIVING,

    typeOfMovementWithinFacility: sectionData?.typeOfMovementWithinFacility || '',
    movementWithinFacilityDetails: sectionData?.movementWithinFacilityDetails || '',
    movementWithinFacilityAssistanceRequired: sectionData?.movementWithinFacilityAssistanceRequired || '',

    typeOfMovementOutsideFacility: sectionData?.typeOfMovementOutsideFacility || '',
    movementOutsideFacilityDetails: sectionData?.movementOutsideFacilityDetails || '',
    movementOutsideFacilityAssistanceRequired: sectionData?.movementOutsideFacilityAssistanceRequired || '',

    doesEvacuationAddressedInEvacuationPlan: sectionData?.doesEvacuationAddressedInEvacuationPlan || false,

    anyFallRisks: sectionData?.anyFallRisks || false,
    fallRisksDetails: sectionData?.fallRisksDetails || '',
    fallRisksAssistanceRequired: sectionData?.fallRisksAssistanceRequired || '',

    anyFallPreventionPlan: sectionData?.anyFallPreventionPlan || false,
    fallPreventionPlanDetails: sectionData?.fallPreventionPlanDetails || '',
    fallPreventionPlanAssistanceRequired: sectionData?.fallPreventionPlanAssistanceRequired || '',

    doesResidentChooseDoorLocks: sectionData?.doesResidentChooseDoorLocks || false,
    doorLocksDetails: sectionData?.doorLocksDetails || '',
    doorLocksAssistanceRequired: sectionData?.doorLocksAssistanceRequired || '',

    anyEquipmentUsedForMobility: sectionData?.anyEquipmentUsedForMobility || false,
    equipmentUsedForMobilityDetails: sectionData?.equipmentUsedForMobilityDetails || '',
    equipmentUsedForMobilityAssistanceRequired: sectionData?.equipmentUsedForMobilityAssistanceRequired || '',

    equipmentVendorDetails: sectionData?.equipmentVendorDetails || '',

    anyLimitationsInMobility: sectionData?.anyLimitationsInMobility || false,
    limitationsInMobilityDetails: sectionData?.limitationsInMobilityDetails || '',
    limitationsInMobilityAssistanceRequired: sectionData?.limitationsInMobilityAssistanceRequired || '',

    // Bed Mobility/Transfer section
    typeOfBedMobility: sectionData?.typeOfBedMobility || '',
    bedMobilityDetails: sectionData?.bedMobilityDetails || '',
    bedMobilityAssistanceRequired: sectionData?.bedMobilityAssistanceRequired || '',

    anyInBedSkinCare: sectionData?.anyInBedSkinCare || false,
    inBedSkinCareDetails: sectionData?.inBedSkinCareDetails || '',
    inBedSkinCareAssistanceRequired: sectionData?.inBedSkinCareAssistanceRequired || '',

    anyInBedTurningAndRepositioning: sectionData?.anyInBedTurningAndRepositioning || false,
    inBedTurningAndRepositioningDetails: sectionData?.inBedTurningAndRepositioningDetails || '',
    inBedTurningAndRepositioningAssistanceRequired: sectionData?.inBedTurningAndRepositioningAssistanceRequired || '',

    anyInBedSafetyPrecautions: sectionData?.anyInBedSafetyPrecautions || false,
    inBedSafetyPrecautionsDetails: sectionData?.inBedSafetyPrecautionsDetails || '',
    inBedSafetyPrecautionsAssistanceRequired: sectionData?.inBedSafetyPrecautionsAssistanceRequired || '',

    anyInBedRiskForFallSafetyPlan: sectionData?.anyInBedRiskForFallSafetyPlan || false,
    inBedRiskForFallSafetyPlanDetails: sectionData?.inBedRiskForFallSafetyPlanDetails || '',
    inBedRiskForFallSafetyPlanAssistanceRequired: sectionData?.inBedRiskForFallSafetyPlanAssistanceRequired || '',

    anyInBedFallPreventionSafetyPlan: sectionData?.anyInBedFallPreventionSafetyPlan || false,
    inBedFallPreventionSafetyPlanDetails: sectionData?.inBedFallPreventionSafetyPlanDetails || '',
    inBedFallPreventionSafetyPlanAssistanceRequired: sectionData?.inBedFallPreventionSafetyPlanAssistanceRequired || '',

    anyInBedSafetyPlan: sectionData?.anyInBedSafetyPlan || false,
    inBedSafetyPlanDetails: sectionData?.inBedSafetyPlanDetails || '',
    inBedSafetyPlanAssistanceRequired: sectionData?.inBedSafetyPlanAssistanceRequired || '',

    anyInBedMedicalDevices: sectionData?.anyInBedMedicalDevices || false,
    inBedMedicalDevicesDetails: sectionData?.inBedMedicalDevicesDetails || '',
    inBedMedicalDevicesAssistanceRequired: sectionData?.inBedMedicalDevicesAssistanceRequired || '',

    anyInBedMedicalEnablers: sectionData?.anyInBedMedicalEnablers || false,
    inBedMedicalEnablersDetails: sectionData?.inBedMedicalEnablersDetails || '',
    inBedMedicalEnablersAssistanceRequired: sectionData?.inBedMedicalEnablersAssistanceRequired || '',

    anyInBedMedicalHoyerLifts: sectionData?.anyInBedMedicalHoyerLifts || false,
    inBedMedicalHoyerLiftsDetails: sectionData?.inBedMedicalHoyerLiftsDetails || '',
    inBedMedicalHoyerLiftsAssistanceRequired: sectionData?.inBedMedicalHoyerLiftsAssistanceRequired || '',

    anyInBedMedicalTransferPole: sectionData?.anyInBedMedicalTransferPole || false,
    inBedMedicalTransferPoleDetails: sectionData?.inBedMedicalTransferPoleDetails || '',
    inBedMedicalTransferPoleAssistanceRequired: sectionData?.inBedMedicalTransferPoleAssistanceRequired || '',

    anyInBedMedicalDeviceAssessment: sectionData?.anyInBedMedicalDeviceAssessment || false,
    inBedMedicalDeviceAssessmentDetails: sectionData?.inBedMedicalDeviceAssessmentDetails || '',
    inBedMedicalDeviceAssessmentAssistanceRequired: sectionData?.inBedMedicalDeviceAssessmentAssistanceRequired || '',

    anyInBedNightTimeCare: sectionData?.anyInBedNightTimeCare || false,
    inBedNightTimeCareDetails: sectionData?.inBedNightTimeCareDetails || '',
    inBedNightTimeCareAssistanceRequired: sectionData?.inBedNightTimeCareAssistanceRequired || '',

    anyInBedEquipmentOrSupplies: sectionData?.anyInBedEquipmentOrSupplies || false,
    inBedEquipmentOrSuppliesDetails: sectionData?.inBedEquipmentOrSuppliesDetails || '',
    inBedEquipmentOrSuppliesAssistanceRequired: sectionData?.inBedEquipmentOrSuppliesAssistanceRequired || '',

    // Eating 
    typeOfEatingBehavior: sectionData?.typeOfEatingBehavior || '',
    eatingBehaviorDetails: sectionData?.eatingBehaviorDetails || '',
    eatingBehaviorAssistanceRequired: sectionData?.eatingBehaviorAssistanceRequired || '',

    anySpecialDietaryNeeds: sectionData?.anySpecialDietaryNeeds || false,
    specialDietaryNeedsDetails: sectionData?.specialDietaryNeedsDetails || '',
    specialDietaryNeedsAssistanceRequired: sectionData?.specialDietaryNeedsAssistanceRequired || '',

    anyEatingHabits: sectionData?.anyEatingHabits || false,
    eatingHabitsDetails: sectionData?.eatingHabitsDetails || '',
    eatingHabitsAssistanceRequired: sectionData?.eatingHabitsAssistanceRequired || '',

    anyFoodAllergies: sectionData?.anyFoodAllergies || false,
    foodAllergiesDetails: sectionData?.foodAllergiesDetails || '',
    foodAllergiesAssistanceRequired: sectionData?.foodAllergiesAssistanceRequired || '',

    anyEatingEquipmentUsed: sectionData?.anyEatingEquipmentUsed || false,
    eatingEquipmentUsedDetails: sectionData?.eatingEquipmentUsedDetails || '',
    eatingEquipmentUsedAssistanceRequired: sectionData?.eatingEquipmentUsedAssistanceRequired || '',

    anyEatingLimitations: sectionData?.anyEatingLimitations || false,
    eatingLimitationsDetails: sectionData?.eatingLimitationsDetails || '',
    eatingLimitationsAssistanceRequired: sectionData?.eatingLimitationsAssistanceRequired || '',

    // Toileting/Continence issues
    typeOfToiletHandling: sectionData?.typeOfToiletHandling || '',
    toiletHandlingDetails: sectionData?.toiletHandlingDetails || '',
    toiletHandlingAssistanceRequired: sectionData?.toiletHandlingAssistanceRequired || '',

    toiletFrequencyDetails: sectionData?.toiletFrequencyDetails || '',
    toiletFrequencyAssistanceRequired: sectionData?.toiletFrequencyAssistanceRequired || '',

    anyBladderContinenceIssues: sectionData?.anyBladderContinenceIssues || false,
    bladderContinenceIssuesDetails: sectionData?.bladderContinenceIssuesDetails || '',
    bladderContinenceIssuesAssistanceRequired: sectionData?.bladderContinenceIssuesAssistanceRequired || '',

    anyBowelContinenceIssues: sectionData?.anyBowelContinenceIssues || false,
    bowelContinenceIssuesDetails: sectionData?.bowelContinenceIssuesDetails || '',
    bowelContinenceIssuesAssistanceRequired: sectionData?.bowelContinenceIssuesAssistanceRequired || '',

    anySkinCareDueToBowelIncontinence: sectionData?.anySkinCareDueToBowelIncontinence || false,
    skinCareDueToBowelIncontinenceDetails: sectionData?.skinCareDueToBowelIncontinenceDetails || '',
    skinCareDueToBowelIncontinenceAssistanceRequired: sectionData?.skinCareDueToBowelIncontinenceAssistanceRequired || '',

    anyToiletingEquipmentUsed: sectionData?.anyToiletingEquipmentUsed || false,
    toiletingEquipmentUsedDetails: sectionData?.toiletingEquipmentUsedDetails || '',
    toiletingEquipmentUsedAssistanceRequired: sectionData?.toiletingEquipmentUsedAssistanceRequired || '',

    anyToiletingLimitations: sectionData?.anyToiletingLimitations || false,
    toiletingLimitationsDetails: sectionData?.toiletingLimitationsDetails || '',
    toiletingLimitationsAssistanceRequired: sectionData?.toiletingLimitationsAssistanceRequired || '',

    // Dressing
    typeOfDressing: sectionData?.typeOfDressing || '',
    dressingDetails: sectionData?.dressingDetails || '',
    dressingAssistanceRequired: sectionData?.dressingAssistanceRequired || '',

    anyDressingEquipmentUsed: sectionData?.anyDressingEquipmentUsed || false,
    dressingEquipmentUsedDetails: sectionData?.dressingEquipmentUsedDetails || '',
    dressingEquipmentUsedAssistanceRequired: sectionData?.dressingEquipmentUsedAssistanceRequired || '',

    anyDressingLimitations: sectionData?.anyDressingLimitations || false,
    dressingLimitationsDetails: sectionData?.dressingLimitationsDetails || '',
    dressingLimitationsAssistanceRequired: sectionData?.dressingLimitationsAssistanceRequired || '',

    // Personal Hygiene
    typeOfPersonalHygiene: sectionData?.typeOfPersonalHygiene || '',
    personalHygieneDetails: sectionData?.personalHygieneDetails || '',
    personalHygieneAssistanceRequired: sectionData?.personalHygieneAssistanceRequired || '',

    teethType: sectionData?.teethType || '',
    teethTypeDetails: sectionData?.teethTypeDetails || '',
    teethTypeAssistanceRequired: sectionData?.teethTypeAssistanceRequired || '',

    anyOralHygieneCare: oralHygieneCareValues,
    oralHygieneCareDetails: sectionData?.oralHygieneCareDetails || '',
    oralHygieneCareAssistanceRequired: sectionData?.oralHygieneCareAssistanceRequired || '',

    anyFlossing: sectionData?.anyFlossing || false,
    flossingDetails: sectionData?.flossingDetails || '',
    flossingAssistanceRequired: sectionData?.flossingAssistanceRequired || '',

    anySoaking: sectionData?.anySoaking || false,
    soakingDetails: sectionData?.soakingDetails || '',
    soakingAssistanceRequired: sectionData?.soakingAssistanceRequired || '',

    anyBrushing: sectionData?.anyBrushing || false,
    brushingDetails: sectionData?.brushingDetails || '',
    brushingAssistanceRequired: sectionData?.brushingAssistanceRequired || '',

    anyHairCare: sectionData?.anyHairCare || false,
    hairCareDetails: sectionData?.hairCareDetails || '',
    hairCareAssistanceRequired: sectionData?.hairCareAssistanceRequired || '',

    anyMensesCare: sectionData?.anyMensesCare || false,
    mensesCareDetails: sectionData?.mensesCareDetails || '',
    mensesCareAssistanceRequired: sectionData?.mensesCareAssistanceRequired || '',

    hygieneFrequencyDetails: sectionData?.hygieneFrequencyDetails || '',
    hygieneFrequencyAssistanceRequired: sectionData?.hygieneFrequencyAssistanceRequired || '',

    anyHygieneEquipmentUsed: sectionData?.anyHygieneEquipmentUsed || false,
    hygieneEquipmentUsedDetails: sectionData?.hygieneEquipmentUsedDetails || '',
    hygieneEquipmentUsedAssistanceRequired: sectionData?.hygieneEquipmentUsedAssistanceRequired || '',

    anyHygieneLimitations: sectionData?.anyHygieneLimitations || false,
    hygieneLimitationsDetails: sectionData?.hygieneLimitationsDetails || '',
    hygieneLimitationsAssistanceRequired: sectionData?.hygieneLimitationsAssistanceRequired || '',

    // Bathing
    typeOfBathing: sectionData?.typeOfBathing || '',
    bathingDetails: sectionData?.bathingDetails || '',
    bathingAssistanceRequired: sectionData?.bathingAssistanceRequired || '',

    bathingFrequencyDetails: sectionData?.bathingFrequencyDetails || '',
    bathingFrequencyAssistanceRequired: sectionData?.bathingFrequencyAssistanceRequired || '',

    anyBathingEquipmentUsed: sectionData?.anyBathingEquipmentUsed || false,
    bathingEquipmentUsedDetails: sectionData?.bathingEquipmentUsedDetails || '',
    bathingEquipmentUsedAssistanceRequired: sectionData?.bathingEquipmentUsedAssistanceRequired || '',

    anyBathingLimitations: sectionData?.anyBathingLimitations || false,
    bathingLimitationsDetails: sectionData?.bathingLimitationsDetails || '',
    bathingLimitationsAssistanceRequired: sectionData?.bathingLimitationsAssistanceRequired || '',

    // Foot care
    typeOfFootCare: sectionData?.typeOfFootCare || '',
    footCareDetails: sectionData?.footCareDetails || '',
    footCareAssistanceRequired: sectionData?.footCareAssistanceRequired || '',

    anyFootCare: sectionData?.anyFootCare || false,
    footCareFrequencyDetails: sectionData?.footCareFrequencyDetails || '',
    footCareFrequencyAssistanceRequired: sectionData?.footCareFrequencyAssistanceRequired || '',

    anyDiabeticFootCare: sectionData?.anyDiabeticFootCare || false,
    diabeticFootCareDetails: sectionData?.diabeticFootCareDetails || '',
    diabeticFootCareAssistanceRequired: sectionData?.diabeticFootCareAssistanceRequired || '',

    anyNailCare: sectionData?.anyNailCare || false,
    nailCareDetails: sectionData?.nailCareDetails || '',
    nailCareAssistanceRequired: sectionData?.nailCareAssistanceRequired || '',

    anyFootCareHomeHealthAgency: sectionData?.anyFootCareHomeHealthAgency || false,
    footCareHomeHealthAgencyDetails: sectionData?.footCareHomeHealthAgencyDetails || '',

    anyFootCareEquipmentUsed: sectionData?.anyFootCareEquipmentUsed || false,
    footCareEquipmentUsedDetails: sectionData?.footCareEquipmentUsedDetails || '',
    footCareEquipmentUsedAssistanceRequired: sectionData?.footCareEquipmentUsedAssistanceRequired || '',

    anyFootCareLimitations: sectionData?.anyFootCareLimitations || false,
    footCareLimitationsDetails: sectionData?.footCareLimitationsDetails || '',
    footCareLimitationsAssistanceRequired: sectionData?.footCareLimitationsAssistanceRequired || '',

    // Skin care
    typeOfSkinCare: sectionData?.typeOfSkinCare || '',
    skinCareDetails: sectionData?.skinCareDetails || '',
    skinCareAssistanceRequired: sectionData?.skinCareAssistanceRequired || '',

    skinCareFrequencyDetails: sectionData?.skinCareFrequencyDetails || '',
    skinCareFrequencyAssistanceRequired: sectionData?.skinCareFrequencyAssistanceRequired || '',

    anySkinProblems: sectionData?.anySkinProblems || false,
    skinProblemsDetails: sectionData?.skinProblemsDetails || '',
    skinProblemsAssistanceRequired: sectionData?.skinProblemsAssistanceRequired || '',

    anyPressureInjuries: sectionData?.anyPressureInjuries || false,
    pressureInjuriesDetails: sectionData?.pressureInjuriesDetails || '',
    pressureInjuriesAssistanceRequired: sectionData?.pressureInjuriesAssistanceRequired || '',

    anyDressingChanges: sectionData?.anyDressingChanges || false,
    dressingChangesDetails: sectionData?.dressingChangesDetails || '',
    dressingChangesAssistanceRequired: sectionData?.dressingChangesAssistanceRequired || '',

    anyNurseDelegationForSkinCare: sectionData?.anyNurseDelegationForSkinCare || false,
    nurseDelegationForSkinCareDetails: sectionData?.nurseDelegationForSkinCareDetails || '',
    nurseDelegationForSkinCareContact: sectionData?.nurseDelegationForSkinCareContact || '',

    anySkinCareHomeHealthAgency: sectionData?.anySkinCareHomeHealthAgency || false,
    skinCareHomeHealthAgencyDetails: sectionData?.skinCareHomeHealthAgencyDetails || '',

    anySkinCareEquipmentUsed: sectionData?.anySkinCareEquipmentUsed || false,
    skinCareEquipmentUsedDetails: sectionData?.skinCareEquipmentUsedDetails || '',
    skinCareEquipmentUsedAssistanceRequired: sectionData?.skinCareEquipmentUsedAssistanceRequired || '',

    anySkinCareLimitations: sectionData?.anySkinCareLimitations || false,
    skinCareLimitationsDetails: sectionData?.skinCareLimitationsDetails || '',
    skinCareLimitationsAssistanceRequired: sectionData?.skinCareLimitationsAssistanceRequired || '',
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, getValues, setValue } = useForm({ defaultValues });
  const watchAnyFallRisks = watch("anyFallRisks", defaultValues.anyFallRisks);
  const watchAnyFallPreventionPlan = watch("anyFallPreventionPlan", defaultValues.anyFallPreventionPlan);
  const watchDoesResidentChooseDoorLocks = watch("doesResidentChooseDoorLocks", defaultValues.doesResidentChooseDoorLocks);
  const watchAnyEquipmentUsedForMobility = watch("anyEquipmentUsedForMobility", defaultValues.anyEquipmentUsedForMobility);
  const watchAnyLimitationsInMobility = watch("anyLimitationsInMobility", defaultValues.anyLimitationsInMobility);

  const watchAnyInBedSkinCare = watch("anyInBedSkinCare", defaultValues.anyInBedSkinCare);
  const watchAnyInBedTurningAndRepositioning = watch("anyInBedTurningAndRepositioning", defaultValues.anyInBedTurningAndRepositioning);
  const watchAnyInBedSafetyPrecautions = watch("anyInBedSafetyPrecautions", defaultValues.anyInBedSafetyPrecautions);
  const watchAnyInBedRiskForFallSafetyPlan = watch("anyInBedRiskForFallSafetyPlan", defaultValues.anyInBedRiskForFallSafetyPlan);
  const watchAnyInBedFallPreventionSafetyPlan = watch("anyInBedFallPreventionSafetyPlan", defaultValues.anyInBedFallPreventionSafetyPlan);
  const watchAnyInBedSafetyPlan = watch("anyInBedSafetyPlan", defaultValues.anyInBedSafetyPlan);
  const watchAnyInBedMedicalDevices = watch("anyInBedMedicalDevices", defaultValues.anyInBedMedicalDevices);
  const watchAnyInBedMedicalEnablers = watch("anyInBedMedicalEnablers", defaultValues.anyInBedMedicalEnablers);
  const watchAnyInBedMedicalHoyerLifts = watch("anyInBedMedicalHoyerLifts", defaultValues.anyInBedMedicalHoyerLifts);
  const watchAnyInBedMedicalTransferPole = watch("anyInBedMedicalTransferPole", defaultValues.anyInBedMedicalTransferPole);
  const watchAnyInBedMedicalDeviceAssessment = watch("anyInBedMedicalDeviceAssessment", defaultValues.anyInBedMedicalDeviceAssessment);
  const watchAnyInBedNightTimeCare = watch("anyInBedNightTimeCare", defaultValues.anyInBedNightTimeCare);
  const watchAnyInBedEquipmentOrSupplies = watch("anyInBedEquipmentOrSupplies", defaultValues.anyInBedEquipmentOrSupplies);

  const watchAnySpecialDietaryNeeds = watch("anySpecialDietaryNeeds", defaultValues.anySpecialDietaryNeeds);
  const watchAnyEatingHabits = watch("anyEatingHabits", defaultValues.anyEatingHabits);
  const watchAnyFoodAllergies = watch("anyFoodAllergies", defaultValues.anyFoodAllergies);
  const watchAnyEatingEquipmentUsed = watch("anyEatingEquipmentUsed", defaultValues.anyEatingEquipmentUsed);
  const watchAnyEatingLimitations = watch("anyEatingLimitations", defaultValues.anyEatingLimitations);

  const watchAnyBladderContinenceIssues = watch("anyBladderContinenceIssues", defaultValues.anyBladderContinenceIssues);
  const watchAnyBowelContinenceIssues = watch("anyBowelContinenceIssues", defaultValues.anyBowelContinenceIssues);
  const watchAnySkinCareDueToBowelIncontinence = watch("anySkinCareDueToBowelIncontinence", defaultValues.anySkinCareDueToBowelIncontinence);
  const watchAnyToiletingEquipmentUsed = watch("anyToiletingEquipmentUsed", defaultValues.anyToiletingEquipmentUsed);
  const watchAnyToiletingLimitations = watch("anyToiletingLimitations", defaultValues.anyToiletingLimitations);

  const watchAnyDressingEquipmentUsed = watch("anyDressingEquipmentUsed", defaultValues.anyDressingEquipmentUsed);
  const watchAnyDressingLimitations = watch("anyDressingLimitations", defaultValues.anyDressingLimitations);

  const watchAnyOralHygieneCare = watch("anyOralHygieneCare", defaultValues.anyOralHygieneCare);
  const watchAnyHairCare = watch("anyHairCare", defaultValues.anyHairCare);
  const watchAnyMensesCare = watch("anyMensesCare", defaultValues.anyMensesCare);
  const watchAnyHygieneEquipmentUsed = watch("anyHygieneEquipmentUsed", defaultValues.anyHygieneEquipmentUsed);
  const watchAnyHygieneLimitations = watch("anyHygieneLimitations", defaultValues.anyHygieneLimitations);

  const watchAnyBathingEquipmentUsed = watch("anyBathingEquipmentUsed", defaultValues.anyBathingEquipmentUsed);
  const watchAnyBathingLimitations = watch("anyBathingLimitations", defaultValues.anyBathingLimitations);

  const watchAnyFootCare = watch("anyFootCare", defaultValues.anyFootCare);
  const watchAnyDiabeticFootCare = watch("anyDiabeticFootCare", defaultValues.anyDiabeticFootCare);
  const watchAnyNailCare = watch("anyNailCare", defaultValues.anyNailCare);
  const watchAnyFootCareEquipmentUsed = watch("anyFootCareEquipmentUsed", defaultValues.anyFootCareEquipmentUsed);
  const watchAnyFootCareLimitations = watch("anyFootCareLimitations", defaultValues.anyFootCareLimitations);
  const watchAnyFootCareHomeHealthAgency = watch("anyFootCareHomeHealthAgency", defaultValues.anyFootCareHomeHealthAgency);

  const watchAnySkinProblems = watch("anySkinProblems", defaultValues.anySkinProblems);
  const watchAnyPressureInjuries = watch("anyPressureInjuries", defaultValues.anyPressureInjuries);
  const watchAnyDressingChanges = watch("anyDressingChanges", defaultValues.anyDressingChanges);
  const watchAnyNurseDelegationForSkinCare = watch("anyNurseDelegationForSkinCare", defaultValues.anyNurseDelegationForSkinCare);
  const watchAnySkinCareEquipmentUsed = watch("anySkinCareEquipmentUsed", defaultValues.anySkinCareEquipmentUsed);
  const watchAnySkinCareLimitations = watch("anySkinCareLimitations", defaultValues.anySkinCareLimitations);
  const watchAnySkinCareHomeHealthAgency = watch("anySkinCareHomeHealthAgency", defaultValues.anySkinCareHomeHealthAgency);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={ADL_GENIE_QUESTIONS}
        fieldDefinitions={ADL_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as ActivitiesOfDailyLivingGenieSectionValues}
        successMessage="Activities of daily living care plan updated."
        errorMessage="Unable to update the activities of daily living care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = { ...getValues(), ...payload.valueUpdates };
          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });
          await onsubmit(nextValues);
        }}
      />

      <Card className="resident-careplan-card" footer={actions()} header={actions()} >
        <div className="">
          <form className="">
            <Accordion multiple>
              <AccordionTab header="Ambulation/Mobility">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_MOBILITY_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_MOBILITY_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Mobility Type - Within facility</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfMovementWithinFacility })}>Locomotion in room and immediate living environment:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfMovementWithinFacility"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select Type of Movement"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfMovementWithinFacility')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="movementWithinFacilityDetails" className={classNames({ 'p-error': errors.movementWithinFacilityDetails })}>
                              Add movement within facility details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="movementWithinFacilityDetails" control={control}
                              rules={{
                                required: 'Movement within facility details are required.',
                                maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE }
                              }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add movement within facility details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('movementWithinFacilityDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="movementWithinFacilityAssistanceRequired" className={classNames({ 'p-error': errors.movementWithinFacilityAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="movementWithinFacilityAssistanceRequired" control={control}
                              rules={{
                                maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE }
                              }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for movement within facility"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('movementWithinFacilityAssistanceRequired')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <label className={classNames({ 'p-error': errors.doesEvacuationAddressedInEvacuationPlan })}>Does evacuation addressed in evacuation plan?</label>
                            <div className="inline-flex m-2 span-color-white">
                              <Controller
                                name="doesEvacuationAddressedInEvacuationPlan"
                                control={control}
                                render={({ field }) => (
                                  <ToggleButton onLabel="Yes" offLabel="No"
                                    {...field}
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.value)} />
                                )}
                              />
                            </div>
                            {getFormErrorMessage('doesEvacuationAddressedInEvacuationPlan')}
                          </div>
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any movement within facility? </strong>{sectionData?.typeOfMovementWithinFacility}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Movement within facility details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.movementWithinFacilityDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with movement within facility: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.movementWithinFacilityAssistanceRequired || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Does evacuation addressed in evacuation plan? </strong>{sectionData?.doesEvacuationAddressedInEvacuationPlan ? YES : NO}</div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Mobility Type - Outside facility</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfMovementOutsideFacility })}>Locomotion outside of immediate living environment (including outdoors):</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfMovementOutsideFacility"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select Type of Movement"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfMovementOutsideFacility')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="movementOutsideFacilityDetails" className={classNames({ 'p-error': errors.movementOutsideFacilityDetails })}>
                              Add movement outside facility details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="movementOutsideFacilityDetails" control={control}
                              rules={{ required: 'Movement outside facility details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add movement outside facility details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('movementOutsideFacilityDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="movementOutsideFacilityAssistanceRequired" className={classNames({ 'p-error': errors.movementOutsideFacilityAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="movementOutsideFacilityAssistanceRequired" control={control}
                              rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for movement outside facility"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('movementOutsideFacilityAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any movement outside facility? </strong>{sectionData?.typeOfMovementOutsideFacility}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Movement outside facility details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.movementOutsideFacilityDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with movement outside facility: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.movementOutsideFacilityAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Ambulation/Mobility - Fall Risks</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFallRisks })}>Any fall risks?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFallRisks"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFallRisks')}
                        </div>

                        {
                          watchAnyFallRisks && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="fallRisksDetails" className={classNames({ 'p-error': errors.fallRisksDetails })}>
                                    Add fall risks details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="fallRisksDetails" control={control}
                                    rules={{ required: 'Fall risks details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add fall risks details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('fallRisksDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="fallRisksAssistanceRequired" className={classNames({ 'p-error': errors.fallRisksAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="fallRisksAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for fall risks"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('fallRisksAssistanceRequired')}
                              </div>
                            </>
                          )}

                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFallPreventionPlan })}>Any fall prevention plan?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFallPreventionPlan"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFallPreventionPlan')}
                        </div>

                        {
                          watchAnyFallPreventionPlan && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="fallPreventionPlanDetails" className={classNames({ 'p-error': errors.fallPreventionPlanDetails })}>
                                    Add fall prevention plan details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="fallPreventionPlanDetails" control={control}
                                    rules={{ required: 'Fall prevention plan details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add fall prevention plan details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('fallPreventionPlanDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="fallPreventionPlanAssistanceRequired" className={classNames({ 'p-error': errors.fallPreventionPlanAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="fallPreventionPlanAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for fall prevention plan"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('fallPreventionPlanAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any fall risks? </strong>{sectionData?.anyFallRisks ? YES : NO}</div>
                        {
                          sectionData?.anyFallRisks && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Fall risks details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.fallRisksDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with fall risks: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.fallRisksAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any fall prevention plan? </strong>{sectionData?.anyFallPreventionPlan ? YES : NO}</div>
                        {
                          sectionData?.anyFallPreventionPlan && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Fall prevention plan details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.fallPreventionPlanDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with fall prevention plan: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.fallPreventionPlanAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Ambulation/Mobility - Door locks</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label className={classNames({ 'p-error': errors.doesResidentChooseDoorLocks })}>Does resident choose door locks?</label>
                            <span className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                              onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_RESIDENT_CHOOSE_DOOR_LOCKS) }}>
                              <i className="pi pi-info-circle"></i>
                            </span>
                          </span>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="doesResidentChooseDoorLocks"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('doesResidentChooseDoorLocks')}
                        </div>

                        {
                          watchDoesResidentChooseDoorLocks && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="doorLocksDetails" className={classNames({ 'p-error': errors.doorLocksDetails })}>
                                    Add door locks details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="doorLocksDetails" control={control}
                                    rules={{ required: 'Door locks details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add door locks details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('doorLocksDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="doorLocksAssistanceRequired" className={classNames({ 'p-error': errors.doorLocksAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="doorLocksAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for door locks"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('doorLocksAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Does resident choose door locks? </strong>{sectionData?.doesResidentChooseDoorLocks ? YES : NO}</div>
                        {
                          sectionData?.doesResidentChooseDoorLocks && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Door locks details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.doorLocksDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with door locks: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.doorLocksAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Ambulation/Mobility - Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyEquipmentUsedForMobility })}>Any equipment used for mobility?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyEquipmentUsedForMobility"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyEquipmentUsedForMobility')}
                        </div>

                        {
                          watchAnyEquipmentUsedForMobility && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="equipmentUsedForMobilityDetails" className={classNames({ 'p-error': errors.equipmentUsedForMobilityDetails })}>
                                    Add equipment used for mobility details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="equipmentUsedForMobilityDetails" control={control}
                                    rules={{ required: 'Equipment used for mobility details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add equipment used for mobility details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('equipmentUsedForMobilityDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="equipmentUsedForMobilityAssistanceRequired" className={classNames({ 'p-error': errors.equipmentUsedForMobilityAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="equipmentUsedForMobilityAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for equipment used for mobility"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('equipmentUsedForMobilityAssistanceRequired')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="equipmentVendorDetails" className={classNames({ 'p-error': errors.equipmentVendorDetails })}>
                                    Vendor details for equipment used for mobility:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="equipmentVendorDetails" control={control}
                                    rules={{ required: 'Vendor details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any vendor details for equipment used for mobility"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('equipmentVendorDetails')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any equipment used for mobility? </strong>{sectionData?.anyEquipmentUsedForMobility ? YES : NO}</div>
                        {
                          sectionData?.anyEquipmentUsedForMobility && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Equipment used for mobility details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.equipmentUsedForMobilityDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with equipment used for mobility: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.equipmentUsedForMobilityAssistanceRequired || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Vendor details for equipment used for mobility: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.equipmentVendorDetails || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Ambulation/Mobility - Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyLimitationsInMobility })}>Does resident have any limitations in mobility?</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyLimitationsInMobility"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyLimitationsInMobility')}
                        </div>

                        {
                          watchAnyLimitationsInMobility && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="limitationsInMobilityDetails" className={classNames({ 'p-error': errors.limitationsInMobilityDetails })}>
                                    Add mobility limitations details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="limitationsInMobilityDetails" control={control}
                                    rules={{ required: 'Mobility limitations details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add mobility limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('limitationsInMobilityDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="limitationsInMobilityAssistanceRequired" className={classNames({ 'p-error': errors.limitationsInMobilityAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="limitationsInMobilityAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for mobility limitations"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('limitationsInMobilityAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Does Resident have any limitations in mobility? </strong>{sectionData?.anyLimitationsInMobility ? YES : NO}</div>
                        {
                          sectionData?.anyLimitationsInMobility && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Mobility limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.limitationsInMobilityDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with mobility limitations: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.limitationsInMobilityAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Bed Mobility/Transfer">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_BED_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_BED_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap">
                  <Divider><Tag severity="warning">Mobility Type</Tag></Divider>
                  <span>Transfer includes moving between bed, chair, wheelchair, standing position - excludes to/from bath/toilet. Bed mobility includes the ability to move in bed.</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfBedMobility })}>Resident mobility in bed:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfBedMobility"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select Type of Movement"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfBedMobility')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="bedMobilityDetails" className={classNames({ 'p-error': errors.bedMobilityDetails })}>
                              Add bed mobility details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="bedMobilityDetails" control={control}
                              rules={{ required: 'Bed mobility details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add bed mobility details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('bedMobilityDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="bedMobilityAssistanceRequired" className={classNames({ 'p-error': errors.bedMobilityAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="bedMobilityAssistanceRequired" control={control}
                              rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for bed mobility"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('bedMobilityAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Bed mobility: </strong>{sectionData?.typeOfBedMobility}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Bed mobility details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bedMobilityDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with bed mobility: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bedMobilityAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Skin Care</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyInBedSkinCare })}>Skin care required due to inability to position self:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyInBedSkinCare"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyInBedSkinCare')}
                        </div>
                        {
                          watchAnyInBedSkinCare && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedSkinCareDetails" className={classNames({ 'p-error': errors.inBedSkinCareDetails })}>
                                    Add in-bed skin care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedSkinCareDetails" control={control}
                                    rules={{ required: 'In-bed skin care details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add in-bed skin care details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedSkinCareDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedSkinCareAssistanceRequired" className={classNames({ 'p-error': errors.inBedSkinCareAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedSkinCareAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for in-bed skin care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedSkinCareAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Skin care required due to inability to position self: </strong>{sectionData?.anyInBedSkinCare ? YES : NO}</div>
                        {
                          sectionData?.anyInBedSkinCare && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>In-bed skin care details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedSkinCareDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with in-bed skin care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedSkinCareAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Turning/Reposition</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyInBedTurningAndRepositioning })}>Any Turning and Repositioning needed:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyInBedTurningAndRepositioning"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyInBedTurningAndRepositioning')}
                        </div>
                        {
                          watchAnyInBedTurningAndRepositioning && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedTurningAndRepositioningDetails" className={classNames({ 'p-error': errors.inBedTurningAndRepositioningDetails })}>
                                    Add in-bed turning and repositioning details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedTurningAndRepositioningDetails" control={control}
                                    rules={{ required: 'In-bed turning and repositioning details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add in-bed turning and repositioning details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedTurningAndRepositioningDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedTurningAndRepositioningAssistanceRequired" className={classNames({ 'p-error': errors.inBedTurningAndRepositioningAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedTurningAndRepositioningAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for in-bed turning and repositioning"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedTurningAndRepositioningAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any in-bed turning and repositioning: </strong>{sectionData?.anyInBedTurningAndRepositioning ? YES : NO}</div>
                        {
                          sectionData?.anyInBedTurningAndRepositioning && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>In-bed turning and repositioning details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedTurningAndRepositioningDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with in-bed turning and repositioning: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedTurningAndRepositioningAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Safety Plan</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyInBedSafetyPrecautions })}>Any safety assessment, alternatives explored; how to keep resident safe:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyInBedSafetyPrecautions"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyInBedSafetyPrecautions')}
                        </div>
                        {
                          watchAnyInBedSafetyPrecautions && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedSafetyPrecautionsDetails" className={classNames({ 'p-error': errors.inBedSafetyPrecautionsDetails })}>
                                    Add in-bed safety precautions details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedSafetyPrecautionsDetails" control={control}
                                    rules={{ required: 'In-bed safety precautions details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add in-bed safety precautions details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedSafetyPrecautionsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedSafetyPrecautionsAssistanceRequired" className={classNames({ 'p-error': errors.inBedSafetyPrecautionsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedSafetyPrecautionsAssistanceRequired" control={control}
                                    rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for in-bed safety precautions"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedSafetyPrecautionsAssistanceRequired')}
                              </div>

                              <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <label className={classNames({ 'p-error': errors.anyInBedRiskForFallSafetyPlan })}>Any safety assessment regarding risks for falls:</label>
                                <div className="inline-flex m-2 span-color-white">
                                  <Controller
                                    name="anyInBedRiskForFallSafetyPlan"
                                    control={control}
                                    render={({ field }) => (
                                      <ToggleButton onLabel="Yes" offLabel="No"
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.value)} />
                                    )}
                                  />
                                </div>
                                {getFormErrorMessage('anyInBedRiskForFallSafetyPlan')}
                              </div>


                              {
                                watchAnyInBedRiskForFallSafetyPlan && (
                                  <>
                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedRiskForFallSafetyPlanDetails" className={classNames({ 'p-error': errors.inBedRiskForFallSafetyPlanDetails })}>
                                          Add in-bed risk for fall safety plan details:
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedRiskForFallSafetyPlanDetails" control={control}
                                          rules={{ required: 'In-bed risk for fall safety plan details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Add in-bed risk for fall safety plan details"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedRiskForFallSafetyPlanDetails')}
                                    </div>

                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedRiskForFallSafetyPlanAssistanceRequired" className={classNames({ 'p-error': errors.inBedRiskForFallSafetyPlanAssistanceRequired })}>
                                          {ASSISTANCE_REQUIRED_MESSAGE}
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedRiskForFallSafetyPlanAssistanceRequired" control={control}
                                          rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Describe any assistance required for in-bed risk for fall safety plan"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedRiskForFallSafetyPlanAssistanceRequired')}
                                    </div>
                                  </>
                                )}

                              <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <label className={classNames({ 'p-error': errors.anyInBedFallPreventionSafetyPlan })}>Any safety assessment regarding fall prevention:</label>
                                <div className="inline-flex m-2 span-color-white">
                                  <Controller
                                    name="anyInBedFallPreventionSafetyPlan"
                                    control={control}
                                    render={({ field }) => (
                                      <ToggleButton onLabel="Yes" offLabel="No"
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.value)} />
                                    )}
                                  />
                                </div>
                                {getFormErrorMessage('anyInBedFallPreventionSafetyPlan')}
                              </div>

                              {
                                watchAnyInBedFallPreventionSafetyPlan && (
                                  <>
                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedFallPreventionSafetyPlanDetails" className={classNames({ 'p-error': errors.inBedFallPreventionSafetyPlanDetails })}>
                                          Add in-bed fall prevention safety plan details:
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedFallPreventionSafetyPlanDetails" control={control}
                                          rules={{ required: 'In-bed fall prevention safety plan details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Add in-bed fall prevention safety plan details"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedFallPreventionSafetyPlanDetails')}
                                    </div>

                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedFallPreventionSafetyPlanAssistanceRequired" className={classNames({ 'p-error': errors.inBedFallPreventionSafetyPlanAssistanceRequired })}>
                                          {ASSISTANCE_REQUIRED_MESSAGE}
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedFallPreventionSafetyPlanAssistanceRequired" control={control}
                                          rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Describe any assistance required for in-bed fall prevention safety plan"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedFallPreventionSafetyPlanAssistanceRequired')}
                                    </div>
                                  </>
                                )}

                              <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <label className={classNames({ 'p-error': errors.anyInBedSafetyPlan })}>Any safety plan:</label>
                                <div className="inline-flex m-2 span-color-white">
                                  <Controller
                                    name="anyInBedSafetyPlan"
                                    control={control}
                                    render={({ field }) => (
                                      <ToggleButton onLabel="Yes" offLabel="No"
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.value)} />
                                    )}
                                  />
                                </div>
                                {getFormErrorMessage('anyInBedSafetyPlan')}
                              </div>

                              {
                                watchAnyInBedSafetyPlan && (
                                  <>
                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedSafetyPlanDetails" className={classNames({ 'p-error': errors.inBedSafetyPlanDetails })}>
                                          Add in-bed safety plan details:
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedSafetyPlanDetails" control={control}
                                          rules={{ required: 'In-bed safety plan details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Add in-bed safety plan details"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedSafetyPlanDetails')}
                                    </div>

                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedSafetyPlanAssistanceRequired" className={classNames({ 'p-error': errors.inBedSafetyPlanAssistanceRequired })}>
                                          {ASSISTANCE_REQUIRED_MESSAGE}
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedSafetyPlanAssistanceRequired" control={control}
                                          rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Describe any assistance required for in-bed safety plan"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedSafetyPlanAssistanceRequired')}
                                    </div>
                                  </>
                                )}

                              <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <label className={classNames({ 'p-error': errors.anyInBedMedicalDevices })}>Any medical device:</label>
                                <div className="inline-flex m-2 span-color-white">
                                  <Controller
                                    name="anyInBedMedicalDevices"
                                    control={control}
                                    render={({ field }) => (
                                      <ToggleButton onLabel="Yes" offLabel="No"
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.value)} />
                                    )}
                                  />
                                </div>
                                {getFormErrorMessage('anyInBedMedicalDevices')}
                              </div>


                              {
                                watchAnyInBedMedicalDevices && (
                                  <>
                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedMedicalDeviceAssessmentDetails" className={classNames({ 'p-error': errors.inBedMedicalDeviceAssessmentDetails })}>
                                          Add in-bed medical device details:
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedMedicalDeviceAssessmentDetails" control={control}
                                          rules={{ required: 'In-bed medical device details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Add in-bed medical device details"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedMedicalDeviceAssessmentDetails')}
                                    </div>

                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedMedicalDeviceAssessmentAssistanceRequired" className={classNames({ 'p-error': errors.inBedMedicalDeviceAssessmentAssistanceRequired })}>
                                          {ASSISTANCE_REQUIRED_MESSAGE}
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedMedicalDeviceAssessmentAssistanceRequired" control={control}
                                          rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Describe any assistance required for in-bed medical devices"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedMedicalDeviceAssessmentAssistanceRequired')}
                                    </div>

                                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <label className={classNames({ 'p-error': errors.anyInBedMedicalEnablers })}>Any enablers:</label>
                                      <div className="inline-flex m-2 span-color-white">
                                        <Controller
                                          name="anyInBedMedicalEnablers"
                                          control={control}
                                          render={({ field }) => (
                                            <ToggleButton onLabel="Yes" offLabel="No"
                                              {...field}
                                              checked={field.value}
                                              onChange={(e) => field.onChange(e.value)} />
                                          )}
                                        />
                                      </div>
                                      {getFormErrorMessage('anyInBedMedicalEnablers')}
                                    </div>

                                    {
                                      watchAnyInBedMedicalEnablers && (
                                        <>
                                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                            <span className="flex align-items-center gap-2 mb-2">
                                              <label htmlFor="inBedMedicalEnablersDetails" className={classNames({ 'p-error': errors.inBedMedicalEnablersDetails })}>
                                                Add enabler details:
                                              </label>
                                            </span>
                                            <span className="p-float-label">
                                              <Controller name="inBedMedicalEnablersDetails" control={control}
                                                rules={{ required: 'In-bed medical enabler details are required.', maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                                render={({ field, fieldState }) => (
                                                  <InputTextarea placeholder="Add in-bed medical enabler details"
                                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                                )} />
                                            </span>
                                            {getFormErrorMessage('inBedMedicalEnablersDetails')}
                                          </div>

                                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                            <span className="flex align-items-center gap-2 mb-2">
                                              <label htmlFor="inBedMedicalEnablersAssistanceRequired" className={classNames({ 'p-error': errors.inBedMedicalEnablersAssistanceRequired })}>
                                                {ASSISTANCE_REQUIRED_MESSAGE}
                                              </label>
                                            </span>
                                            <span className="p-float-label">
                                              <Controller name="inBedMedicalEnablersAssistanceRequired" control={control}
                                                rules={{ maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE } }}
                                                render={({ field, fieldState }) => (
                                                  <InputTextarea placeholder="Describe any assistance required for in-bed medical enablers"
                                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                                )} />
                                            </span>
                                            {getFormErrorMessage('inBedMedicalEnablersAssistanceRequired')}
                                          </div>
                                        </>
                                      )}

                                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <label className={classNames({ 'p-error': errors.anyInBedMedicalHoyerLifts })}>Any Hoyer lifts:</label>
                                      <div className="inline-flex m-2 span-color-white">
                                        <Controller
                                          name="anyInBedMedicalHoyerLifts"
                                          control={control}
                                          render={({ field }) => (
                                            <ToggleButton onLabel="Yes" offLabel="No"
                                              {...field}
                                              checked={field.value}
                                              onChange={(e) => field.onChange(e.value)} />
                                          )}
                                        />
                                      </div>
                                      {getFormErrorMessage('anyInBedMedicalHoyerLifts')}
                                    </div>

                                    {
                                      watchAnyInBedMedicalHoyerLifts && (
                                        <>
                                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                            <span className="flex align-items-center gap-2 mb-2">
                                              <label htmlFor="inBedMedicalHoyerLiftsDetails" className={classNames({ 'p-error': errors.inBedMedicalHoyerLiftsDetails })}>
                                                Add Hoyer lift details:
                                              </label>
                                            </span>
                                            <span className="p-float-label">
                                              <Controller name="inBedMedicalHoyerLiftsDetails" control={control}
                                                rules={{ required: 'In-bed medical Hoyer lift details are required.' }}
                                                render={({ field, fieldState }) => (
                                                  <InputTextarea placeholder="Add in-bed medical Hoyer lift details"
                                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                                )} />
                                            </span>
                                            {getFormErrorMessage('inBedMedicalHoyerLiftsDetails')}
                                          </div>

                                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                            <span className="flex align-items-center gap-2 mb-2">
                                              <label htmlFor="inBedMedicalHoyerLiftsAssistanceRequired" className={classNames({ 'p-error': errors.inBedMedicalHoyerLiftsAssistanceRequired })}>
                                                {ASSISTANCE_REQUIRED_MESSAGE}
                                              </label>
                                            </span>
                                            <span className="p-float-label">
                                              <Controller name="inBedMedicalHoyerLiftsAssistanceRequired" control={control}

                                                render={({ field, fieldState }) => (
                                                  <InputTextarea placeholder="Describe any assistance required for in-bed medical Hoyer lifts"
                                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                                )} />
                                            </span>
                                            {getFormErrorMessage('inBedMedicalHoyerLiftsAssistanceRequired')}
                                          </div>
                                        </>
                                      )}

                                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <label className={classNames({ 'p-error': errors.anyInBedMedicalTransferPole })}>Any transfer pole:</label>
                                      <div className="inline-flex m-2 span-color-white">
                                        <Controller
                                          name="anyInBedMedicalTransferPole"
                                          control={control}
                                          render={({ field }) => (
                                            <ToggleButton onLabel="Yes" offLabel="No"
                                              {...field}
                                              checked={field.value}
                                              onChange={(e) => field.onChange(e.value)} />
                                          )}
                                        />
                                      </div>
                                      {getFormErrorMessage('anyInBedMedicalTransferPole')}
                                    </div>

                                    {
                                      watchAnyInBedMedicalTransferPole && (
                                        <>
                                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                            <span className="flex align-items-center gap-2 mb-2">
                                              <label htmlFor="inBedMedicalTransferPoleDetails" className={classNames({ 'p-error': errors.inBedMedicalTransferPoleDetails })}>
                                                Add transfer pole details:
                                              </label>
                                            </span>
                                            <span className="p-float-label">
                                              <Controller name="inBedMedicalTransferPoleDetails" control={control}
                                                rules={{ required: 'In-bed medical transfer pole details are required.' }}
                                                render={({ field, fieldState }) => (
                                                  <InputTextarea placeholder="Add in-bed medical transfer pole details"
                                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                                )} />
                                            </span>
                                            {getFormErrorMessage('inBedMedicalTransferPoleDetails')}
                                          </div>

                                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                            <span className="flex align-items-center gap-2 mb-2">
                                              <label htmlFor="inBedMedicalTransferPoleAssistanceRequired" className={classNames({ 'p-error': errors.inBedMedicalTransferPoleAssistanceRequired })}>
                                                {ASSISTANCE_REQUIRED_MESSAGE}
                                              </label>
                                            </span>
                                            <span className="p-float-label">
                                              <Controller name="inBedMedicalTransferPoleAssistanceRequired" control={control}

                                                render={({ field, fieldState }) => (
                                                  <InputTextarea placeholder="Describe any assistance required for in-bed medical transfer pole"
                                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                                )} />
                                            </span>
                                            {getFormErrorMessage('inBedMedicalTransferPoleAssistanceRequired')}
                                          </div>
                                        </>
                                      )}
                                  </>
                                )}

                              <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <label className={classNames({ 'p-error': errors.anyInBedNightTimeCare })}>Any night time care:</label>
                                <div className="inline-flex m-2 span-color-white">
                                  <Controller
                                    name="anyInBedNightTimeCare"
                                    control={control}
                                    render={({ field }) => (
                                      <ToggleButton onLabel="Yes" offLabel="No"
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.value)} />
                                    )}
                                  />
                                </div>
                                {getFormErrorMessage('anyInBedNightTimeCare')}
                              </div>
                              {
                                watchAnyInBedNightTimeCare && (
                                  <>
                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedNightTimeCareDetails" className={classNames({ 'p-error': errors.inBedNightTimeCareDetails })}>
                                          Add night care details:
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedNightTimeCareDetails" control={control}
                                          rules={{ required: 'In-bed night time care details are required.' }}
                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Add in-bed night time care details"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedNightTimeCareDetails')}
                                    </div>

                                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                      <span className="flex align-items-center gap-2 mb-2">
                                        <label htmlFor="inBedNightTimeCareAssistanceRequired" className={classNames({ 'p-error': errors.inBedNightTimeCareAssistanceRequired })}>
                                          {ASSISTANCE_REQUIRED_MESSAGE}
                                        </label>
                                      </span>
                                      <span className="p-float-label">
                                        <Controller name="inBedNightTimeCareAssistanceRequired" control={control}

                                          render={({ field, fieldState }) => (
                                            <InputTextarea placeholder="Describe any assistance required for in-bed night time care"
                                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                          )} />
                                      </span>
                                      {getFormErrorMessage('inBedNightTimeCareAssistanceRequired')}
                                    </div>
                                  </>
                                )}

                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any safety assessment, alternatives explored; how to keep resident safe: </strong>{sectionData?.anyInBedSafetyPrecautions ? YES : NO}</div>
                        {
                          sectionData?.anyInBedSafetyPrecautions && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedSafetyPrecautionsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedSafetyPrecautionsAssistanceRequired || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any safety assessment regarding Risks for falls: </strong>{sectionData?.anyInBedRiskForFallSafetyPlan ? YES : NO}</div>
                              {
                                sectionData?.anyInBedRiskForFallSafetyPlan && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedRiskForFallSafetyPlanDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedRiskForFallSafetyPlanAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any safety assessment regarding fall prevention: </strong>{sectionData?.anyInBedFallPreventionSafetyPlan ? YES : NO}</div>
                              {
                                sectionData?.anyInBedFallPreventionSafetyPlan && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedFallPreventionSafetyPlanDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedFallPreventionSafetyPlanAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other safety plan: </strong>{sectionData?.anyInBedSafetyPlan ? YES : NO}</div>
                              {
                                sectionData?.anyInBedSafetyPlan && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedSafetyPlanDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedSafetyPlanAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any medical devices safety plan: </strong>{sectionData?.anyInBedMedicalDevices ? YES : NO}</div>
                              {
                                sectionData?.anyInBedMedicalDevices && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalDeviceAssessmentDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalDeviceAssessmentAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any enablers: </strong>{sectionData?.anyInBedMedicalEnablers ? YES : NO}</div>
                              {
                                sectionData?.anyInBedMedicalEnablers && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalEnablersDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalEnablersAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any hoyer lifts: </strong>{sectionData?.anyInBedMedicalHoyerLifts ? YES : NO}</div>
                              {
                                sectionData?.anyInBedMedicalHoyerLifts && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalHoyerLiftsDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalHoyerLiftsAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any transfer pole: </strong>{sectionData?.anyInBedMedicalTransferPole ? YES : NO}</div>
                              {
                                sectionData?.anyInBedMedicalTransferPole && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Safety plan details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalTransferPoleDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedMedicalTransferPoleAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}

                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any night time care: </strong>{sectionData?.anyInBedNightTimeCare ? YES : NO}</div>
                              {
                                sectionData?.anyInBedNightTimeCare && (
                                  <>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Night time care details: </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedNightTimeCareDetails || NA)}</span>
                                    </div>
                                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedNightTimeCareAssistanceRequired || NA)}</span>
                                    </div>
                                  </>
                                )}
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Equipment/Supplies</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyInBedEquipmentOrSupplies })}>Any equipment/supplies:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyInBedEquipmentOrSupplies"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyInBedEquipmentOrSupplies')}
                        </div>
                        {
                          watchAnyInBedEquipmentOrSupplies && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedEquipmentOrSuppliesDetails" className={classNames({ 'p-error': errors.inBedEquipmentOrSuppliesDetails })}>
                                    Add equipment/supplies details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedEquipmentOrSuppliesDetails" control={control}
                                    rules={{ required: 'Equipment/supplies details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add equipment/supplies details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedEquipmentOrSuppliesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="inBedEquipmentOrSuppliesAssistanceRequired" className={classNames({ 'p-error': errors.inBedEquipmentOrSuppliesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="inBedEquipmentOrSuppliesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for equipment/supplies"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('inBedEquipmentOrSuppliesAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any equipment or supplies in bed: </strong>{sectionData?.anyInBedEquipmentOrSupplies ? YES : NO}</div>
                        {
                          sectionData?.anyInBedEquipmentOrSupplies && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Equipment or supplies details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedEquipmentOrSuppliesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with equipment or supplies: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inBedEquipmentOrSuppliesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Eating">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_EATING_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_EATING_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Eating Level</Tag></Divider>
                  <span>How the Resident eats and drinks (regardless of skill). Includes intake of nourishment by other means (e.g., tube feeding, total parenteral nutrition)</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfEatingBehavior })}>Resident eating level:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfEatingBehavior"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select level of Eating Behavior"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfEatingBehavior')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="eatingBehaviorDetails" className={classNames({ 'p-error': errors.eatingBehaviorDetails })}>
                              Add eating details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="eatingBehaviorDetails" control={control}
                              rules={{ required: 'Eating details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add eating details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('eatingBehaviorDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="eatingBehaviorAssistanceRequired" className={classNames({ 'p-error': errors.eatingBehaviorAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="eatingBehaviorAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for eating behavior"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('eatingBehaviorAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Eating Behavior: </strong>{sectionData?.typeOfEatingBehavior}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Eating details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingBehaviorDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with eating behavior: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingBehaviorAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Special Diet</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySpecialDietaryNeeds })}>Any special dietary needs:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySpecialDietaryNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySpecialDietaryNeeds')}
                        </div>
                        {
                          watchAnySpecialDietaryNeeds && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialDietaryNeedsDetails" className={classNames({ 'p-error': errors.specialDietaryNeedsDetails })}>
                                    Add special dietary needs details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialDietaryNeedsDetails" control={control}
                                    rules={{ required: 'Special dietary needs details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add special dietary needs details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialDietaryNeedsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="specialDietaryNeedsAssistanceRequired" className={classNames({ 'p-error': errors.specialDietaryNeedsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="specialDietaryNeedsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for special dietary needs"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('specialDietaryNeedsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special dietary needs: </strong>{sectionData?.anySpecialDietaryNeeds ? YES : NO}</div>
                        {
                          sectionData?.anySpecialDietaryNeeds && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Special dietary needs details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialDietaryNeedsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with special dietary needs: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specialDietaryNeedsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Eating Habits</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyEatingHabits })}>Any eating habits:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyEatingHabits"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyEatingHabits')}
                        </div>
                        {
                          watchAnyEatingHabits && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="eatingHabitsDetails" className={classNames({ 'p-error': errors.eatingHabitsDetails })}>
                                    Add eating habit details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="eatingHabitsDetails" control={control}
                                    rules={{ required: 'Eating habits details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add eating habits details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('eatingHabitsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="eatingHabitsAssistanceRequired" className={classNames({ 'p-error': errors.eatingHabitsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="eatingHabitsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for eating habits"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('eatingHabitsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any eating habits issues: </strong>{sectionData?.anyEatingHabits ? YES : NO}</div>
                        {
                          sectionData?.anyEatingHabits && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Eating habits issues details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingHabitsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with eating habits issues: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingHabitsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Food Allergies</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFoodAllergies })}>Any food allergies:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFoodAllergies"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFoodAllergies')}
                        </div>
                        {
                          watchAnyFoodAllergies && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="foodAllergiesDetails" className={classNames({ 'p-error': errors.foodAllergiesDetails })}>
                                    Add food allergy details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="foodAllergiesDetails" control={control}
                                    rules={{ required: 'Food allergy details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add food allergy details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('foodAllergiesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="foodAllergiesAssistanceRequired" className={classNames({ 'p-error': errors.foodAllergiesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="foodAllergiesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for food allergies"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('foodAllergiesAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any food allergies: </strong>{sectionData?.anyFoodAllergies ? YES : NO}</div>
                        {
                          sectionData?.anyFoodAllergies && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Food allergy details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.foodAllergiesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with food allergies: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.foodAllergiesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Eating Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyEatingEquipmentUsed })}>Any eating equipments used:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyEatingEquipmentUsed"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyEatingEquipmentUsed')}
                        </div>
                        {
                          watchAnyEatingEquipmentUsed && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="eatingEquipmentUsedDetails" className={classNames({ 'p-error': errors.eatingEquipmentUsedDetails })}>
                                    Add eating equipment details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="eatingEquipmentUsedDetails" control={control}
                                    rules={{ required: 'Eating equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add eating equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('eatingEquipmentUsedDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="eatingEquipmentUsedAssistanceRequired" className={classNames({ 'p-error': errors.eatingEquipmentUsedAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="eatingEquipmentUsedAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for eating equipment"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('eatingEquipmentUsedAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any eating equipment used: </strong>{sectionData?.anyEatingEquipmentUsed ? YES : NO}</div>
                        {
                          sectionData?.anyEatingEquipmentUsed && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Eating equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingEquipmentUsedDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with eating equipment: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingEquipmentUsedAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Eating Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyEatingLimitations })}>Any eating limitations:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyEatingLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyEatingLimitations')}
                        </div>
                        {
                          watchAnyEatingLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="eatingLimitationsDetails" className={classNames({ 'p-error': errors.eatingLimitationsDetails })}>
                                    Add eating limitation details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="eatingLimitationsDetails" control={control}
                                    rules={{ required: 'Eating limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add eating limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('eatingLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="eatingLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.eatingLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="eatingLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for eating limitations"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('eatingLimitationsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any eating limitations: </strong>{sectionData?.anyEatingLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyEatingLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Eating limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with eating: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.eatingLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Toileting/continence">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_TOILET_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_TOILET_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Toileting Level</Tag></Divider>
                  <span>How individual uses the toilet room (or commode, bed pan, urinal); transfers on/off toilet, cleanses, changes incontinence pads, manages ostomy or catheter, adjusts clothes</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfToiletHandling })}>Resident toileting level:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfToiletHandling"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select level of Toileting Handling"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfToiletHandling')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="toiletHandlingDetails" className={classNames({ 'p-error': errors.toiletHandlingDetails })}>
                              Add toileting details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="toiletHandlingDetails" control={control}
                              rules={{ required: 'Toileting details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add toileting details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('toiletHandlingDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="toiletHandlingAssistanceRequired" className={classNames({ 'p-error': errors.toiletHandlingAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="toiletHandlingAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for toileting handling"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('toiletHandlingAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Toilet Handling: </strong>{sectionData?.typeOfToiletHandling}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Toileting details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletHandlingDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with toileting handling: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletHandlingAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Toilet Frequency</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="toiletFrequencyDetails" className={classNames({ 'p-error': errors.toiletFrequencyDetails })}>
                              Add toilet frequency details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="toiletFrequencyDetails" control={control}
                              rules={{ required: 'Toilet frequency details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add toilet frequency details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('toiletFrequencyDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="toiletFrequencyAssistanceRequired" className={classNames({ 'p-error': errors.toiletFrequencyAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="toiletFrequencyAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for toilet frequency"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('toiletFrequencyAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Toilet frequency details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletFrequencyDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with toilet: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletFrequencyAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Bladder Incontinence</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyBladderContinenceIssues })}>Any bladder continence issues:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyBladderContinenceIssues"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyBladderContinenceIssues')}
                        </div>
                        {
                          watchAnyBladderContinenceIssues && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bladderContinenceIssuesDetails" className={classNames({ 'p-error': errors.bladderContinenceIssuesDetails })}>
                                    Add bladder continence issues details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bladderContinenceIssuesDetails" control={control}
                                    rules={{ required: 'Bladder continence issues details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add bladder continence issues details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bladderContinenceIssuesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bladderContinenceIssuesAssistanceRequired" className={classNames({ 'p-error': errors.bladderContinenceIssuesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bladderContinenceIssuesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for bladder continence issues"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bladderContinenceIssuesAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any bladder continence issues: </strong>{sectionData?.anyBladderContinenceIssues ? YES : NO}</div>
                        {
                          sectionData?.anyBladderContinenceIssues && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Bladder continence issues details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bladderContinenceIssuesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with bladder continence issues: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bladderContinenceIssuesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Bowel Incontinence</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyBowelContinenceIssues })}>Any bowel incontinence:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyBowelContinenceIssues"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyBowelContinenceIssues')}
                        </div>
                        {
                          watchAnyBowelContinenceIssues && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bowelContinenceIssuesDetails" className={classNames({ 'p-error': errors.bowelContinenceIssuesDetails })}>
                                    Add bowel continence issues details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bowelContinenceIssuesDetails" control={control}
                                    rules={{ required: 'Bowel continence issues details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add bowel continence issues details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bowelContinenceIssuesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bowelContinenceIssuesAssistanceRequired" className={classNames({ 'p-error': errors.bowelContinenceIssuesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bowelContinenceIssuesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for bowel continence issues"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bowelContinenceIssuesAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any bowel continence issues: </strong>{sectionData?.anyBowelContinenceIssues ? YES : NO}</div>
                        {
                          sectionData?.anyBowelContinenceIssues && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Bowel continence issues details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bowelContinenceIssuesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with bowel continence issues: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bowelContinenceIssuesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Skin Care</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySkinCareDueToBowelIncontinence })}>Skin care due to bowel/bladder incontinence:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySkinCareDueToBowelIncontinence"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySkinCareDueToBowelIncontinence')}
                        </div>
                        {
                          watchAnySkinCareDueToBowelIncontinence && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinCareDueToBowelIncontinenceDetails" className={classNames({ 'p-error': errors.skinCareDueToBowelIncontinenceDetails })}>
                                    Add skin care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinCareDueToBowelIncontinenceDetails" control={control}
                                    rules={{ required: 'Skin care details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add skin care details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinCareDueToBowelIncontinenceDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinCareDueToBowelIncontinenceAssistanceRequired" className={classNames({ 'p-error': errors.skinCareDueToBowelIncontinenceAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinCareDueToBowelIncontinenceAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for toileting"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinCareDueToBowelIncontinenceAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any toilet equipment used: </strong>{sectionData?.anySkinCareDueToBowelIncontinence ? YES : NO}</div>
                        {
                          sectionData?.anySkinCareDueToBowelIncontinence && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Skin care details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareDueToBowelIncontinenceDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with skin care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareDueToBowelIncontinenceAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Toilet Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyToiletingEquipmentUsed })}>Any toilet equipments used:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyToiletingEquipmentUsed"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyToiletingEquipmentUsed')}
                        </div>
                        {
                          watchAnyToiletingEquipmentUsed && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="toiletingEquipmentUsedDetails" className={classNames({ 'p-error': errors.toiletingEquipmentUsedDetails })}>
                                    Add toilet equipment details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="toiletingEquipmentUsedDetails" control={control}
                                    rules={{ required: 'Toileting equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add toileting equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('toiletingEquipmentUsedDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="toiletingEquipmentUsedAssistanceRequired" className={classNames({ 'p-error': errors.toiletingEquipmentUsedAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="toiletingEquipmentUsedAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for toileting"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('toiletingEquipmentUsedAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any toilet equipment used: </strong>{sectionData?.anyToiletingEquipmentUsed ? YES : NO}</div>
                        {
                          sectionData?.anyToiletingEquipmentUsed && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Toileting equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletingEquipmentUsedDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with toileting equipment: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletingEquipmentUsedAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Toilet Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyToiletingLimitations })}>Any toilet limitations:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyToiletingLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyToiletingLimitations')}
                        </div>
                        {
                          watchAnyToiletingLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="toiletingLimitationsDetails" className={classNames({ 'p-error': errors.toiletingLimitationsDetails })}>
                                    Add toilet limitation details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="toiletingLimitationsDetails" control={control}
                                    rules={{ required: 'Toileting limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add toileting limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('toiletingLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="toiletingLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.toiletingLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="toiletingLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for toileting"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('toiletingLimitationsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any toilet limitations: </strong>{sectionData?.anyToiletingLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyToiletingLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Toileting limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletingLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with toileting: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.toiletingLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Dressing">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_DRESSING_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_DRESSING_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Dressing Level</Tag></Divider>
                  <span>How individual puts on, fastens, and takes off all items of street clothing, including donning/removing prosthesis</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfDressing })}>Resident dressing level:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfDressing"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select level of Dressing"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfDressing')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="dressingDetails" className={classNames({ 'p-error': errors.dressingDetails })}>
                              Add dressing details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="dressingDetails" control={control}
                              rules={{ required: 'Dressing details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add dressing details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('dressingDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="dressingAssistanceRequired" className={classNames({ 'p-error': errors.dressingAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="dressingAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for dressing"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('dressingAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Dressing: </strong>{sectionData?.typeOfDressing}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Dressing details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with dressing: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Dressing Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyDressingEquipmentUsed })}>Any dressing equipment used:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyDressingEquipmentUsed"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyDressingEquipmentUsed')}
                        </div>
                        {
                          watchAnyDressingEquipmentUsed && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="dressingEquipmentUsedDetails" className={classNames({ 'p-error': errors.dressingEquipmentUsedDetails })}>
                                    Add dressing details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="dressingEquipmentUsedDetails" control={control}
                                    rules={{ required: 'Dressing equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add dressing equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('dressingEquipmentUsedDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="dressingEquipmentUsedAssistanceRequired" className={classNames({ 'p-error': errors.dressingEquipmentUsedAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="dressingEquipmentUsedAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for dressing"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('dressingEquipmentUsedAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any dressing equipments: </strong>{sectionData?.anyDressingEquipmentUsed ? YES : NO}</div>
                        {
                          sectionData?.anyDressingEquipmentUsed && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Dressing equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingEquipmentUsedDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with dressing: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingEquipmentUsedAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Dressing Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyDressingLimitations })}>Any dressing limitations:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyDressingLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyDressingLimitations')}
                        </div>
                        {
                          watchAnyDressingLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="dressingLimitationsDetails" className={classNames({ 'p-error': errors.dressingLimitationsDetails })}>
                                    Add dressing details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="dressingLimitationsDetails" control={control}
                                    rules={{ required: 'Dressing limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add dressing limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('dressingLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="dressingLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.dressingLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="dressingLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for dressing"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('dressingLimitationsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any dressing limitations: </strong>{sectionData?.anyDressingLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyDressingLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Dressing limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with dressing: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Personal Hygiene/Grooming">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_HYGIENE_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_HYGIENE_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Hygiene Level</Tag></Divider>
                  <span>How individual maintains personal hygiene, including combing hair, brushing teeth, shaving, applying makeup, washing/drying face, hands, and perineum</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfPersonalHygiene })}>Resident hygiene level:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfPersonalHygiene"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select level of Personal Hygiene"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfPersonalHygiene')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="personalHygieneDetails" className={classNames({ 'p-error': errors.personalHygieneDetails })}>
                              Add personal hygiene details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="personalHygieneDetails" control={control}
                              rules={{ required: 'Personal hygiene details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add personal hygiene details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('personalHygieneDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="personalHygieneAssistanceRequired" className={classNames({ 'p-error': errors.personalHygieneAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="personalHygieneAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for personal hygiene"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('personalHygieneAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Personal Hygiene: </strong>{sectionData?.typeOfPersonalHygiene}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Personal Hygiene details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.personalHygieneDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with personal hygiene: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.personalHygieneAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Teeth Type</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.teethType })}>Resident teeth type:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="teethType"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={TEETH_TYPE_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select resident teeth type"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('teethType')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="teethTypeDetails" className={classNames({ 'p-error': errors.teethTypeDetails })}>
                              Add teeth type details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="teethTypeDetails" control={control}
                              rules={{ required: 'Teeth type details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add teeth type details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('teethTypeDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="teethTypeAssistanceRequired" className={classNames({ 'p-error': errors.teethTypeAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="teethTypeAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for teeth type"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('teethTypeAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident teeth type: </strong>{sectionData?.teethType || NA}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Teeth details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.teethTypeDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with teeth: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.teethTypeAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Oral Hygiene</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyOralHygieneCare })}>Any oral hygiene:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyOralHygieneCare"
                              control={control}
                              render={({ field }) => (
                                <MultiSelect
                                  {...field}
                                  value={field.value}
                                  options={ORAL_HYGIENE_OPTIONS}
                                  optionLabel="name"
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select oral hygiene care types"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyOralHygieneCare')}
                        </div>

                        {
                          watchAnyOralHygieneCare && watchAnyOralHygieneCare.length > 0 && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="oralHygieneCareDetails" className={classNames({ 'p-error': errors.oralHygieneCareDetails })}>
                                    Add oral hygiene care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="oralHygieneCareDetails" control={control}
                                    rules={{ required: 'Oral hygiene care details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add oral hygiene care details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('oralHygieneCareDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="oralHygieneCareAssistanceRequired" className={classNames({ 'p-error': errors.oralHygieneCareAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="oralHygieneCareAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for oral hygiene care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('oralHygieneCareAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident oral hygiene care: </strong>{oralHygieneCareValues.length ? oralHygieneCareValues.join(', ') : NA}</div>
                        {
                          oralHygieneCareValues.length > 0 && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Oral hygiene care details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.oralHygieneCareDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with oral hygiene care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.oralHygieneCareAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Hair Care</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyHairCare })}>Any hair care:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyHairCare"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyHairCare')}
                        </div>
                        {
                          watchAnyHairCare && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="hairCareDetails" className={classNames({ 'p-error': errors.hairCareDetails })}>
                                    Add hair care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="hairCareDetails" control={control}
                                    rules={{ required: 'Hair care details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add hair care details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('hairCareDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="hairCareAssistanceRequired" className={classNames({ 'p-error': errors.hairCareAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="hairCareAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for hair care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('hairCareAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any hair care equipments: </strong>{sectionData?.anyHairCare ? YES : NO}</div>
                        {
                          sectionData?.anyHairCare && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Hair care details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hairCareDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with hair care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hairCareAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Menses Care</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyMensesCare })}>Any menses care:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyMensesCare"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyMensesCare')}
                        </div>
                        {
                          watchAnyMensesCare && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="mensesCareDetails" className={classNames({ 'p-error': errors.mensesCareDetails })}>
                                    Add menses care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="mensesCareDetails" control={control}
                                    rules={{ required: 'Menses care details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add menses care details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('mensesCareDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="mensesCareAssistanceRequired" className={classNames({ 'p-error': errors.mensesCareAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="mensesCareAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for menses care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('mensesCareAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any menses care: </strong>{sectionData?.anyMensesCare ? YES : NO}</div>
                        {
                          sectionData?.anyMensesCare && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Menses care details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.mensesCareDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with menses care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.mensesCareAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Hygiene frequency</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="hygieneFrequencyDetails" className={classNames({ 'p-error': errors.hygieneFrequencyDetails })}>
                              How often hygiene is performed:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="hygieneFrequencyDetails" control={control}
                              rules={{ required: 'How often hygiene is performed is required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add how often hygiene is performed"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('hygieneFrequencyDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="hygieneFrequencyAssistanceRequired" className={classNames({ 'p-error': errors.hygieneFrequencyAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="hygieneFrequencyAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for hygiene frequency"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('hygieneFrequencyAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Hygiene frequency details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hygieneFrequencyDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with hygiene frequency: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hygieneFrequencyAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Hygiene Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyHygieneEquipmentUsed })}>Any hygiene equipment used:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyHygieneEquipmentUsed"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyHygieneEquipmentUsed')}
                        </div>
                        {
                          watchAnyHygieneEquipmentUsed && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="hygieneEquipmentUsedDetails" className={classNames({ 'p-error': errors.hygieneEquipmentUsedDetails })}>
                                    Add hygiene details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="hygieneEquipmentUsedDetails" control={control}
                                    rules={{ required: 'Hygiene equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add hygiene equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('hygieneEquipmentUsedDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="hygieneEquipmentUsedAssistanceRequired" className={classNames({ 'p-error': errors.hygieneEquipmentUsedAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="hygieneEquipmentUsedAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for hygiene"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('hygieneEquipmentUsedAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any hygiene equipments: </strong>{sectionData?.anyHygieneEquipmentUsed ? YES : NO}</div>
                        {
                          sectionData?.anyHygieneEquipmentUsed && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Hygiene equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hygieneEquipmentUsedDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with hygiene: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hygieneEquipmentUsedAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Hygiene Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyHygieneLimitations })}>Any hygiene limitations:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyHygieneLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyHygieneLimitations')}
                        </div>
                        {
                          watchAnyHygieneLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="hygieneLimitationsDetails" className={classNames({ 'p-error': errors.hygieneLimitationsDetails })}>
                                    Add hygiene details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="hygieneLimitationsDetails" control={control}
                                    rules={{ required: 'Hygiene limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add hygiene limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('hygieneLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="hygieneLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.hygieneLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="hygieneLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for hygiene"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('hygieneLimitationsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any hygiene limitations: </strong>{sectionData?.anyHygieneLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyHygieneLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Hygiene limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hygieneLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with hygiene: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hygieneLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Bathing">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_BATHING_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_BATHING_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Bathing Level</Tag></Divider>
                  <span>How individual takes full-body shower, sponge bath, and transfer in/out of tub/shower</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfBathing })}>Resident bathing level:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfBathing"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select level of Bathing"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfBathing')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="bathingDetails" className={classNames({ 'p-error': errors.bathingDetails })}>
                              Add bathing details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="bathingDetails" control={control}
                              rules={{ required: 'Bathing details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add bathing details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('bathingDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="bathingAssistanceRequired" className={classNames({ 'p-error': errors.bathingAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="bathingAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for bathing"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('bathingAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Bathing: </strong>{sectionData?.typeOfBathing}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Bathing details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with bathing: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Bathing frequency</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="bathingFrequencyDetails" className={classNames({ 'p-error': errors.bathingFrequencyDetails })}>
                              How often bathing is performed:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="bathingFrequencyDetails" control={control}
                              rules={{ required: 'How often bathing is performed is required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add how often bathing is performed"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('bathingFrequencyDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="bathingFrequencyAssistanceRequired" className={classNames({ 'p-error': errors.bathingFrequencyAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="bathingFrequencyAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for bathing frequency"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('bathingFrequencyAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Bathing frequency details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingFrequencyDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with bathing frequency: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingFrequencyAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Bathing Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyBathingEquipmentUsed })}>Any bathing equipment used:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyBathingEquipmentUsed"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyBathingEquipmentUsed')}
                        </div>
                        {
                          watchAnyBathingEquipmentUsed && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bathingEquipmentUsedDetails" className={classNames({ 'p-error': errors.bathingEquipmentUsedDetails })}>
                                    Add bathing details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bathingEquipmentUsedDetails" control={control}
                                    rules={{ required: 'Bathing equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add bathing equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bathingEquipmentUsedDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bathingEquipmentUsedAssistanceRequired" className={classNames({ 'p-error': errors.bathingEquipmentUsedAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bathingEquipmentUsedAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for bathing"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bathingEquipmentUsedAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any bathing equipments: </strong>{sectionData?.anyBathingEquipmentUsed ? YES : NO}</div>
                        {
                          sectionData?.anyBathingEquipmentUsed && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Bathing equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingEquipmentUsedDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with bathing: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingEquipmentUsedAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Bathing Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyBathingLimitations })}>Any bathing limitations:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyBathingLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyBathingLimitations')}
                        </div>
                        {
                          watchAnyBathingLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bathingLimitationsDetails" className={classNames({ 'p-error': errors.bathingLimitationsDetails })}>
                                    Add bathing details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bathingLimitationsDetails" control={control}
                                    rules={{ required: 'Bathing limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add bathing limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bathingLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="bathingLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.bathingLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="bathingLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for bathing"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('bathingLimitationsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any bathing limitations: </strong>{sectionData?.anyBathingLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyBathingLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Bathing limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with bathing: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bathingLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Foot Care">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_FOOT_CARE_GUIDANCE) }}>
                      Section guidance <i className="pi pi-info-circle"></i>
                    </span>
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                  <div className="p-float-label justify-content-end" style={{ cursor: 'pointer' }}
                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_FOOT_CARE_ASSISTANCE) }}>
                    Section assistance guidance <i className="pi pi-info-circle"></i>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Foot Care Level</Tag></Divider>
                  <span>How individual takes care of their foot</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfFootCare })}>Resident foot care level:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfFootCare"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select level of Foot Care"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfFootCare')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="footCareDetails" className={classNames({ 'p-error': errors.footCareDetails })}>
                              Add foot care details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="footCareDetails" control={control}
                              rules={{ required: 'Foot care details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add foot care details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('footCareDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="footCareAssistanceRequired" className={classNames({ 'p-error': errors.footCareAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="footCareAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for foot care"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('footCareAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Foot Care: </strong>{sectionData?.typeOfFootCare}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Foot Care details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with foot care: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Foot Care frequency</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFootCare })}>Any foot care required:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFootCare"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFootCare')}
                        </div>

                        {
                          watchAnyFootCare && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="footCareFrequencyDetails" className={classNames({ 'p-error': errors.footCareFrequencyDetails })}>
                                    How often foot care is performed:
                                  </label>
                                  <span className="p-float-label" style={{ cursor: 'pointer' }}
                                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_FOOT_CARE) }}>
                                    <i className="pi pi-info-circle"></i>
                                  </span>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="footCareFrequencyDetails" control={control}
                                    rules={{ required: 'How often foot care is performed is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add how often foot care is performed"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('footCareFrequencyDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="footCareFrequencyAssistanceRequired" className={classNames({ 'p-error': errors.footCareFrequencyAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="footCareFrequencyAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for foot care frequency"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('footCareFrequencyAssistanceRequired')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any foot care required: </strong>{sectionData?.anyFootCare ? YES : NO}</div>
                        {
                          sectionData?.anyFootCare && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Foot care frequency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareFrequencyDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with foot care frequency: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareFrequencyAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Diabetic Foot Care</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyDiabeticFootCare })}>Any diabetic foot care required:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyDiabeticFootCare"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyDiabeticFootCare')}
                        </div>

                        {
                          watchAnyDiabeticFootCare && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="diabeticFootCareDetails" className={classNames({ 'p-error': errors.diabeticFootCareDetails })}>
                                    How often diabetic foot care is performed:
                                  </label>
                                  <span className="p-float-label" style={{ cursor: 'pointer' }}
                                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ACTIVITY_DIABETIC_FOOT_CARE) }}>
                                    <i className="pi pi-info-circle"></i>
                                  </span>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="diabeticFootCareDetails" control={control}
                                    rules={{ required: 'How often diabetic foot care is performed is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add how often diabetic foot care is performed"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('diabeticFootCareDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="diabeticFootCareAssistanceRequired" className={classNames({ 'p-error': errors.diabeticFootCareAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="diabeticFootCareAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for diabetic foot care frequency"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('diabeticFootCareAssistanceRequired')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any diabetic foot care required: </strong>{sectionData?.anyDiabeticFootCare ? YES : NO}</div>
                        {
                          sectionData?.anyDiabeticFootCare && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Diabetic foot care frequency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.diabeticFootCareDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with diabetic foot care frequency: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.diabeticFootCareAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Nail Care</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyNailCare })}>Any nail care required:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyNailCare"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyNailCare')}
                        </div>

                        {
                          watchAnyNailCare && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="nailCareDetails" className={classNames({ 'p-error': errors.nailCareDetails })}>
                                    How often nail care is performed:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="nailCareDetails" control={control}
                                    rules={{ required: 'How often nail care is performed is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add how often nail care is performed"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('nailCareDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="nailCareAssistanceRequired" className={classNames({ 'p-error': errors.nailCareAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="nailCareAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for nail care frequency"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('nailCareAssistanceRequired')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any nail care required: </strong>{sectionData?.anyNailCare ? YES : NO}</div>
                        {
                          sectionData?.anyNailCare && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Nail care frequency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nailCareDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with nail care frequency: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nailCareAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Home Health Agency</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFootCareHomeHealthAgency })}>Any home health agency:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFootCareHomeHealthAgency"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFootCareHomeHealthAgency')}
                        </div>

                        {
                          watchAnyFootCareHomeHealthAgency && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="footCareHomeHealthAgencyDetails" className={classNames({ 'p-error': errors.footCareHomeHealthAgencyDetails })}>
                                    Add home health agency details:
                                  </label>
                                  <span className="p-float-label" style={{ cursor: 'pointer' }}
                                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.HOME_HEALTH_AGENCY_DETAILS) }}>
                                    <i className="pi pi-info-circle"></i>
                                  </span>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="footCareHomeHealthAgencyDetails" control={control}
                                    rules={{ required: 'Home health agency details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add home health agency details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('footCareHomeHealthAgencyDetails')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any home health agency: </strong>{sectionData?.anyFootCareHomeHealthAgency ? YES : NO}</div>
                        {
                          sectionData?.anyFootCareHomeHealthAgency && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Home health agency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareHomeHealthAgencyDetails || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Foot Care Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFootCareEquipmentUsed })}>Any foot care equipment used:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFootCareEquipmentUsed"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFootCareEquipmentUsed')}
                        </div>
                        {
                          watchAnyFootCareEquipmentUsed && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="footCareEquipmentUsedDetails" className={classNames({ 'p-error': errors.footCareEquipmentUsedDetails })}>
                                    Add foot care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="footCareEquipmentUsedDetails" control={control}
                                    rules={{ required: 'Foot care equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add foot care equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('footCareEquipmentUsedDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="footCareEquipmentUsedAssistanceRequired" className={classNames({ 'p-error': errors.footCareEquipmentUsedAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="footCareEquipmentUsedAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for foot care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('footCareEquipmentUsedAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any foot care equipments: </strong>{sectionData?.anyFootCareEquipmentUsed ? YES : NO}</div>
                        {
                          sectionData?.anyFootCareEquipmentUsed && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Foot care equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareEquipmentUsedDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with foot care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareEquipmentUsedAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Foot Care Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyFootCareLimitations })}>Any foot care limitations:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyFootCareLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyFootCareLimitations')}
                        </div>
                        {
                          watchAnyFootCareLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="footCareLimitationsDetails" className={classNames({ 'p-error': errors.footCareLimitationsDetails })}>
                                    Add foot care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="footCareLimitationsDetails" control={control}
                                    rules={{ required: 'Foot care limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add foot care limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('footCareLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="footCareLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.footCareLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="footCareLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for foot care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('footCareLimitationsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any foot care limitations: </strong>{sectionData?.anyFootCareLimitations ? YES : NO}</div>
                        {
                          sectionData?.anyFootCareLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Foot care limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with foot care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.footCareLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
              <AccordionTab header="Skin Care">
                <div className="flex justify-content-between m-2">
                  <div className="justify-content-start">
                    <span className="p-float-label" style={{ cursor: 'pointer' }}
                      onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.ADL_SELF_PERFORMANCE_CODE_DEFINITIONS) }}>
                      ADL Self-Performance Code Definitions <i className="pi pi-info-circle"></i>
                    </span>
                  </div>
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Skin Care Level</Tag></Divider>
                  <span>How individual take care of their skin</span>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.typeOfSkinCare })}>Resident skin care level:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="typeOfSkinCare"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  {...field}
                                  value={field.value}
                                  options={ACTIVITY_MOBILITY_OPTIONS}
                                  onChange={(e) => field.onChange(e.value)}
                                  placeholder="Select level of Skin Care"
                                />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('typeOfSkinCare')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="skinCareDetails" className={classNames({ 'p-error': errors.skinCareDetails })}>
                              Add skin care details:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="skinCareDetails" control={control}
                              rules={{ required: 'Skin care details are required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add skin care details"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('skinCareDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="skinCareAssistanceRequired" className={classNames({ 'p-error': errors.skinCareAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="skinCareAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for skin care"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('skinCareAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Skin Care: </strong>{sectionData?.typeOfSkinCare}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Skin Care details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with skin care: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Skin Care Frequency</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="skinCareFrequencyDetails" className={classNames({ 'p-error': errors.skinCareFrequencyDetails })}>
                              How often skin care is performed:
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="skinCareFrequencyDetails" control={control}
                              rules={{ required: 'How often skin care is performed is required.' }}
                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Add how often skin care is performed"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('skinCareFrequencyDetails')}
                        </div>

                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <span className="flex align-items-center gap-2 mb-2">
                            <label htmlFor="skinCareFrequencyAssistanceRequired" className={classNames({ 'p-error': errors.skinCareFrequencyAssistanceRequired })}>
                              {ASSISTANCE_REQUIRED_MESSAGE}
                            </label>
                          </span>
                          <span className="p-float-label">
                            <Controller name="skinCareFrequencyAssistanceRequired" control={control}

                              render={({ field, fieldState }) => (
                                <InputTextarea placeholder="Describe any assistance required for skin care frequency"
                                  style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                          </span>
                          {getFormErrorMessage('skinCareFrequencyAssistanceRequired')}
                        </div>
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Skin care frequency details: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareFrequencyDetails || NA)}</span>
                        </div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with skin care frequency: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareFrequencyAssistanceRequired || NA)}</span>
                        </div>
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Skin Problems</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySkinProblems })}>Any skin problems:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySkinProblems"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySkinProblems')}
                        </div>

                        {
                          watchAnySkinProblems && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinProblemsDetails" className={classNames({ 'p-error': errors.skinProblemsDetails })}>
                                    How often skin problems are observed:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinProblemsDetails" control={control}
                                    rules={{ required: 'How often skin problems are observed is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add how often skin problems are observed"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinProblemsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinProblemsAssistanceRequired" className={classNames({ 'p-error': errors.skinProblemsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinProblemsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for skin problems"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinProblemsAssistanceRequired')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any skin problems observed: </strong>{sectionData?.anySkinProblems ? YES : NO}</div>
                        {
                          sectionData?.anySkinProblems && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Skin problems frequency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinProblemsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with skin problems frequency: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinProblemsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Pressure Injuries</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyPressureInjuries })}>Any pressure injuries observed:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyPressureInjuries"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyPressureInjuries')}
                        </div>

                        {
                          watchAnyPressureInjuries && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="pressureInjuriesDetails" className={classNames({ 'p-error': errors.pressureInjuriesDetails })}>
                                    How often pressure injuries are observed:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="pressureInjuriesDetails" control={control}
                                    rules={{ required: 'How often pressure injuries are observed is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add how often pressure injuries are observed"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('pressureInjuriesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="pressureInjuriesAssistanceRequired" className={classNames({ 'p-error': errors.pressureInjuriesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="pressureInjuriesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for pressure injuries"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('pressureInjuriesAssistanceRequired')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any pressure injuries observed: </strong>{sectionData?.anyPressureInjuries ? YES : NO}</div>
                        {
                          sectionData?.anyPressureInjuries && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Pressure injuries frequency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.pressureInjuriesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with pressure injuries frequency: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.pressureInjuriesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Dressing Changes</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyDressingChanges })}>Any dressing changes required:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyDressingChanges"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyDressingChanges')}
                        </div>

                        {
                          watchAnyDressingChanges && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="dressingChangesDetails" className={classNames({ 'p-error': errors.dressingChangesDetails })}>
                                    How often dressing changes are performed:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="dressingChangesDetails" control={control}
                                    rules={{ required: 'How often dressing changes are performed is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add how often dressing changes are performed"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('dressingChangesDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="dressingChangesAssistanceRequired" className={classNames({ 'p-error': errors.dressingChangesAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="dressingChangesAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for dressing changes frequency"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('dressingChangesAssistanceRequired')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any dressing changes required: </strong>{sectionData?.anyDressingChanges ? YES : NO}</div>
                        {
                          sectionData?.anyDressingChanges && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Dressing changes frequency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingChangesDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with dressing changes frequency: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dressingChangesAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Nurse Delegation</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyNurseDelegationForSkinCare })}>Any nurse delegation for skin care required:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyNurseDelegationForSkinCare"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyNurseDelegationForSkinCare')}
                        </div>

                        {
                          watchAnyNurseDelegationForSkinCare && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="nurseDelegationForSkinCareDetails" className={classNames({ 'p-error': errors.nurseDelegationForSkinCareDetails })}>
                                    How often nurse delegation for skin care is performed:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="nurseDelegationForSkinCareDetails" control={control}
                                    rules={{ required: 'How often nurse delegation for skin care is performed is required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add how often nurse delegation for skin care is performed"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('nurseDelegationForSkinCareDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="nurseDelegationForSkinCareContact" className={classNames({ 'p-error': errors.nurseDelegationForSkinCareContact })}>
                                    Contact details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="nurseDelegationForSkinCareContact" control={control}
                                    rules={{ required: 'Contact details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Contact details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('nurseDelegationForSkinCareContact')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any nurse delegation: </strong>{sectionData?.anyNurseDelegationForSkinCare ? YES : NO}</div>
                        {
                          sectionData?.anyNurseDelegationForSkinCare && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Nurse delegation for skin care frequency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nurseDelegationForSkinCareDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Nurse contact details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nurseDelegationForSkinCareContact || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Home Health Agency</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySkinCareHomeHealthAgency })}>Any home health agency:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySkinCareHomeHealthAgency"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySkinCareHomeHealthAgency')}
                        </div>

                        {
                          watchAnySkinCareHomeHealthAgency && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinCareHomeHealthAgencyDetails" className={classNames({ 'p-error': errors.skinCareHomeHealthAgencyDetails })}>
                                    Add home health agency details:
                                  </label>
                                  <span className="p-float-label" style={{ cursor: 'pointer' }}
                                    onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.HOME_HEALTH_AGENCY_DETAILS) }}>
                                    <i className="pi pi-info-circle"></i>
                                  </span>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinCareHomeHealthAgencyDetails" control={control}
                                    rules={{ required: 'Home health agency details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add home health agency details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinCareHomeHealthAgencyDetails')}
                              </div>
                            </>
                          )
                        }
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any home health agency: </strong>{sectionData?.anySkinCareHomeHealthAgency ? YES : NO}</div>
                        {
                          sectionData?.anySkinCareHomeHealthAgency && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Home health agency details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareHomeHealthAgencyDetails || NA)}</span>
                              </div>
                            </>
                          )}
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Skin Care Equipments</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySkinCareEquipmentUsed })}>Any skin care equipment used:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySkinCareEquipmentUsed"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySkinCareEquipmentUsed')}
                        </div>
                        {
                          watchAnySkinCareEquipmentUsed && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinCareEquipmentUsedDetails" className={classNames({ 'p-error': errors.skinCareEquipmentUsedDetails })}>
                                    Add skin care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinCareEquipmentUsedDetails" control={control}
                                    rules={{ required: 'Skin care equipment details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add skin care equipment details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinCareEquipmentUsedDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinCareEquipmentUsedAssistanceRequired" className={classNames({ 'p-error': errors.skinCareEquipmentUsedAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinCareEquipmentUsedAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for skin care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinCareEquipmentUsedAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any skin care equipments: </strong>{sectionData?.anySkinCareEquipmentUsed ? YES : NO}</div>
                        {
                          sectionData?.anySkinCareEquipmentUsed && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Skin care equipment details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareEquipmentUsedDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with skin care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareEquipmentUsedAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>

                <div className="flex-column flex-wrap ">
                  <Divider><Tag severity="warning">Skin Care Limitations</Tag></Divider>
                  {
                    enableEditing ? (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anySkinCareLimitations })}>Any skin care limitations:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anySkinCareLimitations"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anySkinCareLimitations')}
                        </div>
                        {
                          watchAnySkinCareLimitations && (
                            <>
                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinCareLimitationsDetails" className={classNames({ 'p-error': errors.skinCareLimitationsDetails })}>
                                    Add skin care details:
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinCareLimitationsDetails" control={control}
                                    rules={{ required: 'Skin care limitations details are required.' }}
                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Add skin care limitations details"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinCareLimitationsDetails')}
                              </div>

                              <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                                <span className="flex align-items-center gap-2 mb-2">
                                  <label htmlFor="skinCareLimitationsAssistanceRequired" className={classNames({ 'p-error': errors.skinCareLimitationsAssistanceRequired })}>
                                    {ASSISTANCE_REQUIRED_MESSAGE}
                                  </label>
                                </span>
                                <span className="p-float-label">
                                  <Controller name="skinCareLimitationsAssistanceRequired" control={control}

                                    render={({ field, fieldState }) => (
                                      <InputTextarea placeholder="Describe any assistance required for skin care"
                                        style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                    )} />
                                </span>
                                {getFormErrorMessage('skinCareLimitationsAssistanceRequired')}
                              </div>
                            </>
                          )}
                      </div>) : (
                      <div className="flex flex-wrap justify-content-center align-items-center">
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any skin care limitations: </strong>{sectionData?.anySkinCareLimitations ? YES : NO}</div>
                        {
                          sectionData?.anySkinCareLimitations && (
                            <>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Skin care limitations details: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareLimitationsDetails || NA)}</span>
                              </div>
                              <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assistance required with skin care: </strong>
                                <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.skinCareLimitationsAssistanceRequired || NA)}</span>
                              </div>
                            </>
                          )
                        }
                      </div>
                    )
                  }
                </div>
              </AccordionTab>
            </Accordion>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default ActivitiesOfDailyLivingSection;


